import { initializeApp, getApps, getApp } from "firebase/app";
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";
import { WEB_FCM_TOKEN_KEY } from "./utils/constants";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
};

const firebaseConfigValid = () =>
  [
    firebaseConfig.apiKey,
    firebaseConfig.authDomain,
    firebaseConfig.projectId,
    firebaseConfig.messagingSenderId,
    firebaseConfig.appId,
  ].every((value) => typeof value === "string" && value.length > 0);

const getFirebaseApp = () => {
  if (!firebaseConfigValid()) {
    return null;
  }
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
};

/**
 * Whether web FCM can run in this environment (browser + build config + Firebase isSupported()).
 * There is no Firebase "callback" when unsupported — await this once and branch in UI.
 * Cached so repeated checks (e.g. Strict Mode) do not re-run isSupported().
 */
let webPushMessagingSupportedPromise = null;
export const isWebPushMessagingSupported = () => {
  if (!webPushMessagingSupportedPromise) {
    webPushMessagingSupportedPromise = (async () => {
      if (
        typeof window === "undefined" ||
        typeof Notification === "undefined" ||
        !("serviceWorker" in navigator)
      ) {
        return false;
      }
      if (!process.env.REACT_APP_FIREBASE_VAPID_KEY) {
        return false;
      }
      if (!firebaseConfigValid()) {
        return false;
      }
      return isSupported();
    })();
  }
  return webPushMessagingSupportedPromise;
};

/** One in-flight fetch per mode so concurrent callers (e.g. Strict Mode) share a single FCM registration request */
let fcmTokenPromiseNormal = null;
let fcmTokenPromiseForce = null;

export const getWebFcmToken = async ({ forceRefresh = false } = {}) => {
  if (
    typeof window === "undefined" ||
    typeof Notification === "undefined" ||
    !("serviceWorker" in navigator)
  ) {
    return null;
  }

  const cachedToken = localStorage.getItem(WEB_FCM_TOKEN_KEY);
  if (cachedToken && !forceRefresh) {
    return cachedToken;
  }

  const inFlightSlot = forceRefresh ? fcmTokenPromiseForce : fcmTokenPromiseNormal;
  if (inFlightSlot) {
    return inFlightSlot;
  }

  const fetchPromise = (async () => {
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      return null;
    }

    const supported = await isSupported();
    if (!supported) {
      return null;
    }

    const app = getFirebaseApp();
    if (!app) {
      return null;
    }

    const permission =
      Notification.permission === "granted"
        ? "granted"
        : await Notification.requestPermission();
    if (permission !== "granted") {
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js"
      );
      const messaging = getMessaging(app);

      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (!token) {
        return null;
      }

      localStorage.setItem(WEB_FCM_TOKEN_KEY, token);
      return token;
    } catch (_error) {
      return null;
    }
  })();

  if (forceRefresh) {
    fcmTokenPromiseForce = fetchPromise;
  } else {
    fcmTokenPromiseNormal = fetchPromise;
  }

  return fetchPromise.finally(() => {
    if (forceRefresh) {
      fcmTokenPromiseForce = null;
    } else {
      fcmTokenPromiseNormal = null;
    }
  });
};

export const subscribeToForegroundMessages = async (callback) => {
  const app = getFirebaseApp();
  if (!app) {
    return () => {};
  }

  const supported = await isSupported();
  if (!supported) {
    return () => {};
  }

  const messaging = getMessaging(app);
  return onMessage(messaging, callback);
};

export const deleteWebFcmToken = async () => {
  const app = getFirebaseApp();
  if (!app) {
    return false;
  }

  const supported = await isSupported();
  if (!supported) {
    return false;
  }

  try {
    const messaging = getMessaging(app);
    return await deleteToken(messaging);
  } catch (_error) {
    return false;
  }
};
